import { Button, Card, message, Modal, Space, Spin, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { DaoProjectItemType, DaoProposalListItemType, DaoProposalType } from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import {
  DAO_DATE_FORMAT,
  DAO_DATETIME_FORMAT,
  DaoAssetStatusEnum,
  DaoPoolTypeEnum,
  DaoProposalStatusEnum,
  DaoSellingVersionEnum,
} from 'constants/dao-voting'
import { FEATURE } from 'constants/feature'
import { useAssetStateMachineContract } from 'hooks/dao-voting/use-asset-state-machine-contract'
import { daoTxErrorMessage } from 'hooks/dao-voting/use-prepared-tx'
import { usePermission } from 'hooks/use-permission'
import ClaimHistoryCard from 'modules/dao-voting/claim-history-card'
import CreatePoolModal from 'modules/dao-voting/create-pool-modal'
import { AssetStatusTag, ProposalStatusTag } from 'modules/dao-voting/dao-status-tag'
import DeleteProposalModal from 'modules/dao-voting/delete-proposal-modal'
import PoolCard from 'modules/dao-voting/pool-card'
import ProjectAvatar from 'modules/dao-voting/project-avatar'
import ProposalDetailModal from 'modules/dao-voting/proposal-detail-modal'
import ProposalFormModal from 'modules/dao-voting/proposal-form-modal'
import ReportSaleResultModal from 'modules/dao-voting/report-sale-result-modal'
import SystemActionsCard from 'modules/dao-voting/system-actions-card'
import { daoApiErrorMessage, getProposalDisplayName } from 'modules/dao-voting/utils'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

import '../style.scss'

/**
 * PTO DAO Detail (SRS 5.2): header + contextual actions, Proposal History,
 * Pool card. All flows (5.3-5.8) open from here as modals.
 */
const DaoVotingDetail = () => {
  const { id } = useParams()
  // MEYREAL matrix: ADMIN views only - mutating actions need CRUD (SUPER_ADMIN / OPERATOR_MEYREAL)
  const { canCRUD } = usePermission()
  const canManage = canCRUD(FEATURE.DAO)
  const { address } = useAccount()
  const { reportHoldingOnChain } = useAssetStateMachineContract()

  const [project, setProject] = useState<DaoProjectItemType | null>(null)
  const [loading, setLoading] = useState(true)

  const [formOpen, setFormOpen] = useState(false)
  const [editingProposal, setEditingProposal] = useState<DaoProposalType | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingProposal, setDeletingProposal] = useState<DaoProposalType | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [viewingProposal, setViewingProposal] = useState<DaoProposalType | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [poolOpen, setPoolOpen] = useState(false)
  /** bumped after a pool is created - remounts System Actions so it refetches GET .../reward-pools */
  const [poolsRefreshKey, setPoolsRefreshKey] = useState(0)
  const [poolType, setPoolType] = useState<DaoPoolTypeEnum>(DaoPoolTypeEnum.REFUND)

  const getProject = useCallback(async () => {
    if (!id) return
    try {
      const res = await AppAdmin.getDaoVotingProjectProposals(id)
      const data = res.data?.data
      if (!data) {
        setProject(null)
        return
      }
      const proposals = (data.proposals || []).map(
        (row: DaoProposalListItemType): DaoProposalType => ({
          _id: row._id,
          projectId: id,
          title: row.title,
          status: row.status,
          contractAddress: row.contractAddress || undefined,
          votingStart: row.votingStartDate ? dayjs(row.votingStartDate).valueOf() : undefined,
          votingEnd: row.votingEndDate ? dayjs(row.votingEndDate).valueOf() : undefined,
          quorum: row.quorumPercent ?? undefined,
          threshold: row.thresholdPercent ?? undefined,
          participation: row.participation,
          description: row.description ?? undefined,
          terms: row.termsAndConditions ?? undefined,
          discussionLinks: row.discussionRoomLinks ?? undefined,
          estimatedSaleTime: row.estimatedSaleTime ?? undefined,
          estimatedSaleTimeUnit: row.estimatedSaleTimeUnit ?? undefined,
        })
      )
      setProject({
        _id: id,
        fullName: data.fullName,
        thumbnail: data.thumbnail || undefined,
        totalSupply: data.totalSupply ?? 0,
        totalSold: data.totalSold ?? undefined,
        sellingPrice: data.sellingPrice ?? 0,
        sellingVersion: data.sellingVersion ?? null,
        assetStatus: data.assetStatus,
        saleOutcome: null,
        proposals,
        pool: data.pool ?? null,
        lastUpdated: data.lastUpdatedAt ? dayjs(data.lastUpdatedAt).valueOf() : 0,
      })
    } catch (error) {
      logError('Error fetching DAO project detail: ', error)
      setProject(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    getProject()
  }, [getProject])

  const latestProposal = project?.proposals?.[0]

  // sale-result API is per-proposal: the Passed one is awaiting its outcome (SRS 5.8)
  const passedProposal =
    project?.proposals?.find((item) => item.status === DaoProposalStatusEnum.PASSED) ??
    latestProposal

  /** Contextual buttons per asset status (SRS 5.2) */
  const canCreateProposal = useMemo(() => {
    if (!project) return false
    const status = project.assetStatus
    if (status === DaoAssetStatusEnum.HOLDING) return true
    if (
      status === DaoAssetStatusEnum.VOTING &&
      latestProposal?.status === DaoProposalStatusEnum.REJECTED
    ) {
      return true
    }
    if (status === DaoAssetStatusEnum.SALE_FAILED) return true
    return false
  }, [project, latestProposal])

  const openCreate = () => {
    setEditingProposal(null)
    setFormOpen(true)
  }

  const openEdit = (proposal: DaoProposalType) => {
    setDetailOpen(false)
    setEditingProposal(proposal)
    setFormOpen(true)
  }

  const openDelete = (proposal: DaoProposalType) => {
    setDeletingProposal(proposal)
    setDeleteOpen(true)
  }

  const openDetail = (proposal: DaoProposalType) => {
    setViewingProposal(proposal)
    setDetailOpen(true)
  }

  const openPool = (type: DaoPoolTypeEnum) => {
    setPoolType(type)
    setPoolOpen(true)
  }

  /** a new pool changes both the project and GET .../reward-pools */
  const handlePoolCreated = () => {
    getProject()
    setPoolsRefreshKey((key) => key + 1)
  }

  /**
   * Sale Failed -> Update Status: report the asset back to Voting/Holding so a
   * new proposal can be created. BE records it and prepares the
   * AssetStateMachine.reportHolding(collection) calldata for the manager wallet.
   */
  const handleUpdateStatus = () => {
    if (!passedProposal?._id) {
      message.error('No passed proposal found for this project')
      return
    }
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    Modal.confirm({
      title: 'Update Status',
      content: `Report ${project?.fullName} back to Voting so a new proposal can be created?`,
      okText: 'Update Status',
      onOk: async () => {
        try {
          onOpenModalProcessing()
          const res = await AppAdmin.reportDaoVotingHolding(passedProposal._id)
          const holdingCall = res.data?.data?.onChainReportHoldingCall
          if (!holdingCall) {
            throw new Error('Server did not return the on-chain report holding call')
          }
          await reportHoldingOnChain(address, holdingCall)
          message.success('Update status successfully')
          getProject()
        } catch (error: any) {
          logError('Error reporting holding: ', error)
          message.error(
            daoApiErrorMessage(error) || daoTxErrorMessage(error) || 'Failed to update status'
          )
        } finally {
          onCloseModalProcessing()
        }
      },
    })
  }

  const columns: ColumnsType<DaoProposalType> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>
          {project ? getProposalDisplayName(project, record) : record.title}
        </span>
      ),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (address, record) =>
        record.status === DaoProposalStatusEnum.DRAFT || !address ? (
          'Not deployed'
        ) : (
          <Space size={0}>
            <LinkAddress address={address} />
            <ButtonCopy text={address} />
          </Space>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <ProposalStatusTag status={status} />,
    },
    {
      title: 'Voting Period',
      key: 'votingPeriod',
      render: (_, record) => {
        if (!record.votingStart || !record.votingEnd) return '—'
        return `${dayjs(record.votingStart).format(DAO_DATETIME_FORMAT)} — ${dayjs(
          record.votingEnd
        ).format(DAO_DATETIME_FORMAT)}`
      },
    },
    {
      title: 'Quorum',
      dataIndex: 'quorum',
      key: 'quorum',
      render: (quorum) => (quorum == null ? '—' : `${quorum}%`),
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      key: 'threshold',
      render: (threshold) => (threshold == null ? '—' : `${threshold}%`),
    },
    {
      title: 'Participation',
      key: 'participation',
      render: (_, record) => (record.participation == null ? '—' : `${record.participation}%`),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const isDraft = record.status === DaoProposalStatusEnum.DRAFT
        const isScheduled = record.status === DaoProposalStatusEnum.SCHEDULED
        const isPublishing = record.status === DaoProposalStatusEnum.PUBLISHING
        // PTO ERC-3643 cancel tx not confirmed - no edit, Delete retries the cancel
        const isDeleting = record.status === DaoProposalStatusEnum.DELETING
        // no contract on-chain yet (propose tx failed/skipped) - always editable
        // so the admin can fix and re-publish, whatever the computed status is
        const isNotDeployed = !isDraft && !isDeleting && !record.contractAddress
        const canEdit = isScheduled || isPublishing || isNotDeployed
        return (
          <Space size="small">
            {canManage && isDraft ? (
              <Button size="small" type="primary" ghost onClick={() => openEdit(record)}>
                Edit Draft
              </Button>
            ) : null}
            {canManage && canEdit ? (
              <Button size="small" type="primary" ghost onClick={() => openEdit(record)}>
                Edit
              </Button>
            ) : null}
            {!isDraft && !isPublishing ? (
              <Button size="small" onClick={() => openDetail(record)}>
                View Details
              </Button>
            ) : null}
            {canManage && (isDraft || canEdit || isDeleting) && !isPublishing ? (
              <Button size="small" danger onClick={() => openDelete(record)}>
                Delete
              </Button>
            ) : null}
          </Space>
        )
      },
    },
  ]

  if (loading) {
    return (
      <div className="main-section" style={{ textAlign: 'center', paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!project) {
    return <div className="main-section">Project not found</div>
  }

  const isErc3643 = project.sellingVersion === DaoSellingVersionEnum.ERC3643
  // one pool per PTO, forever - a deployed one hides Create Pool even while
  // the asset status has not caught up yet
  const hasPoolOnChain = !!project.pool?.contractAddress

  return (
    <div className="main-section dao-voting-page">
      <Breadcrumb additionalItems={[BREADCRUMBS.DAO_VOTING, { title: project.fullName }]} />

      <div className="dao-detail-header">
        <div>
          <div className="dao-detail-title">
            <ProjectAvatar name={project.fullName} src={project.thumbnail} size={48} />
            <span className="dao-detail-name">{project.fullName}</span>
            <AssetStatusTag status={project.assetStatus} />
          </div>
          <div className="dao-detail-sub">
            {[
              project.totalSupply
                ? `${project.totalSupply} eligible ${isErc3643 ? 'PTOs' : 'NFTs'}`
                : null,
              project.lastUpdated
                ? `Last updated ${dayjs(project.lastUpdated).format(DAO_DATE_FORMAT)}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {canManage ? (
          <Space>
            {canCreateProposal ? (
              // Sale Failed: creating a new proposal is blocked until the asset
              // is reported back to Holding via Update Status
              <Button
                onClick={openCreate}
                disabled={project.assetStatus === DaoAssetStatusEnum.SALE_FAILED}
              >
                Create New Proposal
              </Button>
            ) : null}
            {project.assetStatus === DaoAssetStatusEnum.AWAITING_SALE_RESULT ? (
              <Button className="dao-view-detail-btn" onClick={() => setReportOpen(true)}>
                Report Sale Result
              </Button>
            ) : null}
            {project.assetStatus === DaoAssetStatusEnum.SALE_FAILED ? (
              <>
                <Button className="dao-view-detail-btn" onClick={handleUpdateStatus}>
                  Update Status
                </Button>
                {!hasPoolOnChain ? (
                  <Button
                    className="dao-view-detail-btn"
                    onClick={() => openPool(DaoPoolTypeEnum.REFUND)}
                  >
                    Create Refund Pool
                  </Button>
                ) : null}
              </>
            ) : null}
            {project.assetStatus === DaoAssetStatusEnum.SALE_SUCCESS && !hasPoolOnChain ? (
              <Button
                className="dao-view-detail-btn"
                onClick={() => openPool(DaoPoolTypeEnum.REWARDS)}
              >
                Create Rewards Pool
              </Button>
            ) : null}
          </Space>
        ) : null}
      </div>

      <div className="dao-section-title">Proposal History</div>
      <div className="table-row">
        <Card>
          <Table
            columns={columns}
            dataSource={project.proposals}
            rowKey={(record) => record._id}
            pagination={false}
            locale={{ emptyText: 'No proposals have been created yet' }}
            scroll={{ x: 1100 }}
          />
        </Card>
      </div>

      {/* only show once the deploy tx landed - a pool without contract is not on-chain yet */}
      {project.pool?.contractAddress ? (
        <>
          <div className="dao-section-title">Pool</div>
          <PoolCard pool={project.pool} isErc3643={isErc3643} canManage={canManage} />
        </>
      ) : null}

      {/* System Actions (SRS 5.8/5.9) - once the sale result has been reported */}
      {[
        DaoAssetStatusEnum.SALE_SUCCESS,
        DaoAssetStatusEnum.SALE_FAILED,
        DaoAssetStatusEnum.REFUND_PERIOD,
        DaoAssetStatusEnum.REWARD_PERIOD,
        DaoAssetStatusEnum.ENDED,
      ].includes(project.assetStatus) && id ? (
        <SystemActionsCard key={poolsRefreshKey} tokenizeId={id} isErc3643={isErc3643} />
      ) : null}

      {/* Claim Rewards/Refund History (SRS 5.10) - once a claim window opened */}
      {[
        DaoAssetStatusEnum.REFUND_PERIOD,
        DaoAssetStatusEnum.REWARD_PERIOD,
        DaoAssetStatusEnum.ENDED,
      ].includes(project.assetStatus) && id ? (
        <ClaimHistoryCard tokenizeId={id} />
      ) : null}

      <ProposalFormModal
        open={formOpen}
        project={project}
        proposal={editingProposal}
        onClose={() => setFormOpen(false)}
        onSuccess={getProject}
      />
      <DeleteProposalModal
        open={deleteOpen}
        project={project}
        proposal={deletingProposal}
        onClose={() => setDeleteOpen(false)}
        onSuccess={getProject}
      />
      <ProposalDetailModal
        open={detailOpen}
        project={project}
        proposal={viewingProposal}
        onClose={() => setDetailOpen(false)}
        onEdit={openEdit}
      />
      <ReportSaleResultModal
        open={reportOpen}
        project={project}
        proposal={passedProposal}
        onClose={() => setReportOpen(false)}
        onSuccess={getProject}
      />
      <CreatePoolModal
        open={poolOpen}
        project={project}
        poolType={poolType}
        onClose={() => setPoolOpen(false)}
        onSuccess={handlePoolCreated}
      />
    </div>
  )
}

export default DaoVotingDetail
